import { useCallback, useEffect, useState } from 'react';

import { FILTER_CONFIG } from '@app/components/molecules/map-active-filters/map-active-filters.config';
import {
    AMENITIES_LABELS,
    BEACH_DISTANCE_OPTIONS,
    FILTER_CATEGORY_LABELS,
    FILTER_KEYS,
    PROPERTY_TYPE_LABELS,
    PROVISION_LABELS,
    RATING_LABELS,
    RESERVATION_FEATURES_LABELS,
    ROOM_EQUIPMENT_LABELS,
} from '@app/consts';
import { processArrayFilter, processFilter } from '@app/helpers/filters.helpers';
import { isSupportedAmenityCheck } from '@app/helpers/listing-mapping.helpers';
import { useSearch } from '@app/hooks/useSearch';
import { ActiveFilterModel, SearchFilters } from '@app/models';

/**
 * Hook for active filters
 */
export function useActiveFilters() {
    const {
        filters,
        updateSearchFilters,
        toggleFiltersVisibility,
        isFiltersContentVisible,
        isMobileRes,
    } = useSearch();
    const [activeFilters, setActiveFilters] = useState<ActiveFilterModel[]>([]);
    const [localIsFiltersContentVisible, setLocalIsFiltersContentVisible] =
        useState(isFiltersContentVisible);
    const [isToggleLoading, setIsToggleLoading] = useState(false);

    useEffect(() => {
        setLocalIsFiltersContentVisible(isFiltersContentVisible);
    }, [isFiltersContentVisible]);

    useEffect(() => {
        const filterItems = [
            ...processFilter(
                filters.onlineBooking,
                FILTER_KEYS.ONLINE_BOOKING,
                FILTER_CATEGORY_LABELS.ONLINE_BOOKING,
                value => FILTER_CATEGORY_LABELS.ONLINE_BOOKING,
            ),
            ...processArrayFilter(
                filters.propertyType,
                FILTER_KEYS.PROPERTY_TYPE,
                FILTER_CATEGORY_LABELS.PROPERTY_TYPE,
                PROPERTY_TYPE_LABELS,
            ),
            ...processArrayFilter(
                filters.amenities?.filter(amenity => isSupportedAmenityCheck(parseInt(amenity))),
                FILTER_KEYS.AMENITIES,
                FILTER_CATEGORY_LABELS.AMENITIES,
                AMENITIES_LABELS,
            ),
            ...processArrayFilter(
                filters.roomEquipment,
                FILTER_KEYS.ROOM_EQUIPMENT,
                FILTER_CATEGORY_LABELS.ROOM_EQUIPMENT,
                ROOM_EQUIPMENT_LABELS,
            ),
            ...processArrayFilter(
                filters.provision,
                FILTER_KEYS.PROVISION,
                FILTER_CATEGORY_LABELS.PROVISION,
                PROVISION_LABELS,
            ),
            ...processArrayFilter(
                filters.reservationFeatures,
                FILTER_KEYS.RESERVATION_FEATURES,
                FILTER_CATEGORY_LABELS.RESERVATION_FEATURES,
                RESERVATION_FEATURES_LABELS,
            ),
            ...processFilter(
                filters.rating,
                FILTER_KEYS.RATING,
                FILTER_CATEGORY_LABELS.RATING,
                value => {
                    return RATING_LABELS.find(rating => rating.value === value)?.label || '';
                },
            ),
            ...processFilter(
                filters.priceFrom,
                FILTER_KEYS.PRICE_FROM,
                FILTER_CATEGORY_LABELS.PRICE_FROM,
                value => `${FILTER_CATEGORY_LABELS.PRICE_FROM}: ${value}`,
            ),
            ...processFilter(
                filters.priceTo,
                FILTER_KEYS.PRICE_TO,
                FILTER_CATEGORY_LABELS.PRICE_TO,
                value => `${FILTER_CATEGORY_LABELS.PRICE_TO}: ${value}`,
            ),
            ...processFilter(
                filters.beachDistance,
                FILTER_KEYS.BEACH_DISTANCE,
                FILTER_CATEGORY_LABELS.BEACH_DISTANCE,
                value => {
                    const option = BEACH_DISTANCE_OPTIONS.find(
                        option => option.value === String(value),
                    );

                    return `${FILTER_CATEGORY_LABELS.BEACH_DISTANCE}: ${option?.label ?? `Do ${value} m`}`;
                },
            ),
        ];

        setActiveFilters(filterItems);
    }, [filters]);

    const activeFiltersCount = activeFilters.length;

    const handleRemoveFilter = useCallback(
        (filter: ActiveFilterModel) => {
            const updatedFilters: Partial<SearchFilters> = {};

            if (FILTER_CONFIG.arrayFilterKeys.includes(filter.key)) {
                const propertyKey = FILTER_CONFIG.arrayFilters[filter.key];
                const currentValues = filters[propertyKey];

                if (Array.isArray(currentValues)) {
                    updatedFilters[propertyKey] = currentValues.filter(
                        item => item !== filter.value,
                    );
                }
            } else if (FILTER_CONFIG.scalarFilterKeys.includes(filter.key)) {
                const propertyKey = FILTER_CONFIG.scalarFilters[filter.key];
                updatedFilters[propertyKey] = null;
            } else if (FILTER_CONFIG.stringFilterKeys.includes(filter.key)) {
                const propertyKey = FILTER_CONFIG.stringFilters[filter.key];
                updatedFilters[propertyKey] = null;
            }

            setTimeout(() => updateSearchFilters(updatedFilters, true), 0);
        },
        [filters, updateSearchFilters],
    );

    const handleClearAllFilters = useCallback(() => {
        const clearedFilters = FILTER_CONFIG.defaults.getDefaultFilters();
        updateSearchFilters(clearedFilters, true);
    }, [updateSearchFilters]);

    const handleToggleFilters = useCallback(() => {
        if (isMobileRes) {
            setIsToggleLoading(true);
        }

        setTimeout(() => {
            setLocalIsFiltersContentVisible(prev => !prev);
            toggleFiltersVisibility();

            if (isMobileRes) {
                setIsToggleLoading(false);
            }
        }, 0);
    }, [toggleFiltersVisibility, isMobileRes]);

    return {
        activeFilters,
        activeFiltersCount,
        isToggleLoading,
        localIsFiltersContentVisible,
        handleRemoveFilter,
        handleClearAllFilters,
        handleToggleFilters,
    };
}
